/**
 * 开卡选择验证方式页面（虚拟卡和实体卡通用）
 * 通过 URL 参数 type 区分：electronic（虚拟卡）或 physical（实体卡）
 */

import { useCallback, useEffect } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toast } from 'react-vant'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import SelectVerification, { VerActionType } from '@/components/SelectVerification'

type CardType = 'electronic' | 'physical'

/**
 * 路由状态参数
 */
interface SelectVerificationState {
    /** 开卡参数 */
    applyData: {
        pay_type: 'usdt' | 'usd1' | 'usdc' | 'usd'
        provider?: string
        address_id?: number // 实体卡需要
        activationCode?: string // 兑换码
        kyc_id?: number // 持卡人KYC ID
        product_id?: number
    }
    /** 产品数据 */
    product?: ModelsCardProduct
}

export default function ApplyCardSelectVerification() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()

    // 从 URL 参数获取卡片类型
    const cardType = (searchParams.get('type') as CardType) || 'electronic'

    // 从路由状态中获取参数
    const state = location.state as SelectVerificationState | null

    // 如果没有传递必要参数，返回上一页
    useEffect(() => {
        if (!state || !state.applyData) {
            console.error('缺少开卡参数')
            Toast.fail(t('参数错误'))
            navigate(-1)
            return
        }

        // 实体卡需要邮寄地址
        if (cardType === 'physical' && !state.applyData.address_id) {
            console.error('实体卡缺少邮寄地址')
            Toast.fail(t('请填写邮寄地址'))
            navigate(-1)
            return
        }
    }, [state, cardType, navigate, t])

    // 处理验证方式选择成功后的回调
    const handleVerificationSuccess = useCallback(({ actionType, account }: { actionType: VerActionType; account: string }) => {
        navigate(`/card-v2/apply-card/verify-code?type=${cardType}`, {
            state: {
                applyData: state?.applyData,
                verification: actionType,
                account, // 邮箱/手机账户（谷歌验证时为空）
                product: state?.product, // 传递产品数据
            }
        })
    }, [navigate, cardType, state])

    if (!state) {
        return null
    }

    // 根据卡片类型获取验证码场景
    const scene = 'card_apply'

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
            />
            <div className="px-5 pt-6">
                <SelectVerification
                    scene={scene}
                    onSuccess={handleVerificationSuccess}
                    showPhoneOption={true}
                />
            </div>
        </PageLayout>
    )
}

